import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiShoppingCart, FiHeart, FiShield, FiTruck, FiRefreshCw, FiMinus, FiPlus, FiUser, FiStar, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { getProduct, getProductReviews, addReview } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import StarRating from '../components/common/StarRating';
import './ProductDetailPage.css';

const picsum = (seed) => `https://picsum.photos/seed/${seed}/600/500`;

const DEMO_PRODUCT = {
  _id: 'demo',
  name: 'Premium Wireless Noise-Cancelling Headphones Pro X',
  price: 249.99,
  salePrice: 149.99,
  description: `Experience audio like never before with our Premium Wireless Headphones. Featuring 40 hours of battery life, industry-leading active noise cancellation, and premium drivers that deliver studio-quality sound.\n\nWhether you're commuting, working, or relaxing at home, these headphones adapt to your environment with Smart Ambient mode, letting in just the right amount of sound when you need it.\n\nThe ergonomic over-ear design and memory foam ear cushions ensure comfort during even the longest listening sessions.`,
  images: [
    picsum('headphones1'), picsum('headphones2'),
    picsum('headphones3'), picsum('headphones4'),
  ],
  rating: 4.8,
  totalReviews: 312,
  stock: 42,
  sku: 'HP-PRO-X-BLK',
  vendor: { _id: 'v1', storeName: 'TechZone', storeSlug: 'techzone', rating: 4.7, isVerified: true },
  category: { name: 'Electronics', slug: 'electronics' },
  tags: ['wireless', 'noise-cancelling', 'bluetooth', 'headphones'],
  isFeatured: true,
  shipping: { isFreeShipping: true, shippingCost: 0 },
};

const DEMO_REVIEWS = [
  { _id: 'r1', user: { name: 'Alex M.', avatar: '' }, rating: 5, title: 'Best headphones I\'ve owned', comment: 'The sound quality is exceptional and noise cancellation works flawlessly. Battery lasts way longer than advertised. Worth every penny!', createdAt: '2026-05-15', isVerifiedPurchase: true },
  { _id: 'r2', user: { name: 'Sarah K.', avatar: '' }, rating: 4, title: 'Great value, minor cons', comment: 'Incredible sound for the price. The ear cups are a bit firm at first but get comfortable after a few days of use. Would recommend!', createdAt: '2026-04-22', isVerifiedPurchase: true },
  { _id: 'r3', user: { name: 'Jordan T.', avatar: '' }, rating: 5, title: 'Perfect for travel', comment: 'I travel frequently for work and these headphones have been a game-changer. ANC blocks out engine noise completely on flights.', createdAt: '2026-03-10', isVerifiedPurchase: false },
];

const FEATURES = [
  { icon: <FiTruck />, label: 'Free Shipping', sub: 'On orders over $50' },
  { icon: <FiRefreshCw />, label: '30-Day Returns', sub: 'Hassle-free returns' },
  { icon: <FiShield />, label: '2-Year Warranty', sub: 'Manufacturer guarantee' },
];

const STAR_LABELS = ['Terrible', 'Poor', 'Average', 'Good', 'Excellent'];

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem, getItemQuantity } = useCart();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [wished, setWished] = useState(false);

  // Review form
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        getProduct(id),
        getProductReviews(id),
      ]);
      setProduct(pRes.data.product);
      setReviews(rRes.data.reviews);
    } catch {
      setProduct(DEMO_PRODUCT);
      setReviews(DEMO_REVIEWS);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); setActiveImg(0); setQuantity(1); }, [fetchData]);

  const handleAddToCart = () => {
    if (!product) return;
    addItem(product, quantity);
    toast.success(
      <span><strong>{product.name.slice(0, 24)}…</strong> ×{quantity} added to cart</span>
    );
  };

  const handleBuyNow = () => {
    handleAddToCart();
    window.location.href = '/cart';
  };

  const handleWishlist = () => {
    if (!user) return toast.error('Login to use wishlist');
    setWished((w) => !w);
    toast.success(wished ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error('Please login to leave a review');
    if (!reviewRating) return toast.error('Please select a rating');
    if (reviewComment.trim().length < 10) return toast.error('Comment must be at least 10 characters');

    setSubmitting(true);
    try {
      await addReview({ product: id, rating: reviewRating, title: reviewTitle, comment: reviewComment });
      toast.success('Review submitted!');
      setReviewRating(0);
      setReviewTitle('');
      setReviewComment('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '60vh' }}>
        <div className="spinner spinner-lg" />
        <span>Loading product…</span>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="empty-state" style={{ minHeight: '60vh' }}>
        <div className="empty-state-icon">😕</div>
        <h3>Product not found</h3>
        <Link to="/products" className="btn btn-primary">Browse Products</Link>
      </div>
    );
  }

  const {
    name, price, salePrice, description, images, rating, totalReviews,
    stock, sku, vendor, category, tags, shipping,
  } = product;

  const activePrice = salePrice || price;
  const discount = salePrice ? Math.round((1 - salePrice / price) * 100) : 0;
  const imgList = images?.length ? images : [picsum(name), picsum(name + '2'), picsum(name + '3')];
  const inCartQty = getItemQuantity(product._id);

  return (
    <div className="pdp-page">
      {/* Breadcrumb */}
      <div className="pdp-breadcrumb container">
        <Link to="/">Home</Link>
        <FiChevronRight size={13} />
        <Link to="/products">Products</Link>
        {category && (
          <>
            <FiChevronRight size={13} />
            <Link to={`/products?category=${category._id}`}>{category.name}</Link>
          </>
        )}
        <FiChevronRight size={13} />
        <span>{name.slice(0, 30)}{name.length > 30 ? '…' : ''}</span>
      </div>

      {/* Main */}
      <div className="container pdp-main">
        {/* Gallery */}
        <div className="pdp-gallery">
          <div className="pdp-main-img-wrap">
            <AnimatePresence mode="wait">
              <motion.img
                key={activeImg}
                src={imgList[activeImg]}
                alt={name}
                className="pdp-main-img"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onError={(e) => { e.target.src = picsum(name); }}
              />
            </AnimatePresence>
            {discount > 0 && <span className="pdp-discount-badge">-{discount}% OFF</span>}
          </div>
          <div className="pdp-thumbnails">
            {imgList.map((img, i) => (
              <button
                key={i}
                className={`pdp-thumb ${i === activeImg ? 'active' : ''}`}
                onClick={() => setActiveImg(i)}
              >
                <img src={img} alt={`View ${i + 1}`} onError={(e) => { e.target.src = picsum(name + i); }} />
              </button>
            ))}
          </div>
        </div>

        {/* Info Panel */}
        <div className="pdp-info">
          {/* Vendor */}
          {vendor && (
            <Link to={`/vendors/${vendor._id}`} className="pdp-vendor">
              <span>{vendor.storeName}</span>
              {vendor.isVerified && <span className="pdp-verified">✓ Verified</span>}
            </Link>
          )}

          {category && (
            <span className="pdp-category">{category.name}</span>
          )}

          <h1 className="pdp-title">{name}</h1>

          <div className="pdp-rating-row">
            <StarRating rating={rating} totalReviews={totalReviews} size={15} />
            {inCartQty > 0 && (
              <span className="pdp-in-cart">✓ {inCartQty} in cart</span>
            )}
          </div>

          {/* Price */}
          <div className="pdp-price-row">
            <span className="pdp-price">${activePrice.toFixed(2)}</span>
            {salePrice && (
              <>
                <span className="pdp-price-orig">${price.toFixed(2)}</span>
                <span className="badge badge-danger" style={{ fontSize: '0.8rem' }}>Save ${(price - salePrice).toFixed(2)}</span>
              </>
            )}
          </div>

          {/* Stock */}
          <div className={`pdp-stock ${stock === 0 ? 'out' : stock < 10 ? 'low' : 'in'}`}>
            {stock === 0 ? '✗ Out of Stock' : stock < 10 ? `⚠ Only ${stock} left` : '✓ In Stock'}
          </div>

          {sku && <div className="pdp-sku">SKU: <strong>{sku}</strong></div>}

          {/* Quantity */}
          {stock > 0 && (
            <div className="pdp-qty-row">
              <label className="pdp-qty-label">Quantity</label>
              <div className="qty-stepper">
                <button
                  className="qty-btn"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  <FiMinus size={14} />
                </button>
                <span className="qty-value">{quantity}</span>
                <button
                  className="qty-btn"
                  onClick={() => setQuantity((q) => Math.min(stock, q + 1))}
                  disabled={quantity >= stock}
                >
                  <FiPlus size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pdp-actions">
            <button
              className="btn btn-primary btn-lg"
              style={{ flex: 1 }}
              onClick={handleAddToCart}
              disabled={stock === 0}
            >
              <FiShoppingCart size={17} />
              Add to Cart
            </button>
            <button
              className="btn btn-orange btn-lg"
              style={{ flex: 1 }}
              onClick={handleBuyNow}
              disabled={stock === 0}
            >
              Buy Now
            </button>
            <motion.button
              className={`pdp-wish-btn ${wished ? 'wished' : ''}`}
              onClick={handleWishlist}
              whileTap={{ scale: 0.88 }}
            >
              <FiHeart size={18} />
            </motion.button>
          </div>

          {/* Features */}
          <div className="pdp-features">
            {FEATURES.map(({ icon, label, sub }) => (
              <div key={label} className="pdp-feature">
                <span className="pdp-feature-icon">{icon}</span>
                <div>
                  <p className="pdp-feature-label">{label}</p>
                  <p className="pdp-feature-sub">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tags */}
          {tags?.length > 0 && (
            <div className="pdp-tags">
              {tags.map((t) => (
                <span key={t} className="pdp-tag">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="container pdp-lower">
        <section className="pdp-section">
          <h2 className="pdp-section-title">Product Description</h2>
          <div className="pdp-description">
            {description.split('\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="pdp-section">
          <div className="reviews-header">
            <h2 className="pdp-section-title">
              Customer Reviews
              <span className="reviews-count-badge">{reviews.length}</span>
            </h2>
            {totalReviews > 0 && (
              <div className="overall-rating">
                <span className="overall-score">{rating.toFixed(1)}</span>
                <div>
                  <StarRating rating={rating} size={16} showCount={false} />
                  <p className="overall-sub">{totalReviews} reviews</p>
                </div>
              </div>
            )}
          </div>

          {/* Review Form */}
          {user ? (
            <form className="review-form card card-body" onSubmit={handleReviewSubmit}>
              <h3 className="review-form-title">Write a Review</h3>

              {/* Star Selector */}
              <div className="form-group">
                <label className="form-label">Your Rating *</label>
                <div className="star-selector">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`star-select-btn ${s <= (reviewHover || reviewRating) ? 'active' : ''}`}
                      onMouseEnter={() => setReviewHover(s)}
                      onMouseLeave={() => setReviewHover(0)}
                      onClick={() => setReviewRating(s)}
                      title={STAR_LABELS[s - 1]}
                    >
                      ★
                    </button>
                  ))}
                  {(reviewHover || reviewRating) > 0 && (
                    <span className="star-label">{STAR_LABELS[(reviewHover || reviewRating) - 1]}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Review Title</label>
                <input
                  className="form-input"
                  placeholder="Summarize your experience"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Your Review *</label>
                <textarea
                  className="form-textarea"
                  rows={4}
                  placeholder="Tell others what you think about this product..."
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  minLength={10}
                  maxLength={1000}
                  required
                />
                <span className="form-hint">{reviewComment.length}/1000</span>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !reviewRating}
                style={{ alignSelf: 'flex-start' }}
              >
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </form>
          ) : (
            <div className="review-login-prompt card card-body">
              <FiUser size={28} />
              <p>Please <Link to="/login">login</Link> to leave a review.</p>
            </div>
          )}

          {/* Reviews List */}
          <div className="reviews-list">
            {reviews.length === 0 ? (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <div className="empty-state-icon" style={{ fontSize: '2rem' }}>💬</div>
                <h3>No reviews yet</h3>
                <p>Be the first to review this product.</p>
              </div>
            ) : (
              reviews.map((rev) => (
                <div key={rev._id} className="review-card card">
                  <div className="review-header">
                    <div className="reviewer-info">
                      <div className="avatar avatar-sm">
                        {rev.user?.avatar
                          ? <img src={rev.user.avatar} alt={rev.user.name} />
                          : rev.user?.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="reviewer-name">{rev.user?.name || 'Anonymous'}</p>
                        <p className="review-date">
                          {new Date(rev.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <StarRating rating={rev.rating} size={13} showCount={false} />
                      {rev.isVerifiedPurchase && (
                        <span className="verified-badge">✓ Verified Purchase</span>
                      )}
                    </div>
                  </div>
                  {rev.title && <h4 className="review-title">{rev.title}</h4>}
                  <p className="review-comment">{rev.comment}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
